import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { Navbar } from '../components/Navbar';
import { Avatar, AvatarColor, Button, PipetteIcon, Icon } from '../components/core';
import { updateAvatarColor } from '../services/appUser.service';
import { adjustBrightness } from '../utils/color';

const LANGUAGE_CODES = ['en', 'ar', 'he'] as const;

const AVATAR_COLORS: { id: AvatarColor; label: string }[] = [
  { id: 'coral', label: 'Coral' },
  { id: 'mint', label: 'Mint' },
  { id: 'purple', label: 'Purple' },
  { id: 'sky', label: 'Sky' },
  { id: 'rose', label: 'Rose' },
  { id: 'yellow', label: 'Amber' },
  { id: 'sage', label: 'Sage' },
  { id: 'peach', label: 'Peach' },
  { id: 'teal', label: 'Cyan' },
];

interface UserProfileState {
  displayName: string;
  email: string;
  currentColor: AvatarColor;
  selectedColor: AvatarColor | null;
  customHex: string;
  originalCustomHex: string;
  usingCustomColor: boolean;
}

export default function UserProfilePage() {
  const { user, signOut } = useAuth();
  const { appUser, refetchAppUser } = usePermissions();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const colorInputRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<UserProfileState>(() => {
    const customHex = !appUser ? '#a3e635' : (appUser.customAvatarColor ?? '#a3e635');
    const hasCustom = !appUser ? false : !!appUser.customAvatarColor;

    if (!appUser) {
      return {
        displayName: '',
        email: '',
        currentColor: 'mint',
        selectedColor: null,
        customHex,
        originalCustomHex: customHex,
        usingCustomColor: hasCustom,
      };
    }
    return {
      displayName: appUser.displayName,
      email: appUser.email,
      currentColor: appUser.avatarColor,
      selectedColor: null,
      customHex,
      originalCustomHex: customHex,
      usingCustomColor: hasCustom,
    };
  });

  const [isSaving, setIsSaving] = useState(false);

  if (!appUser) {
    navigate('/login');
    return null;
  }

  function getInitials(displayName: string): string {
    if (!displayName?.trim()) return '?';
    const parts = displayName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  const handleColorSelect = (color: AvatarColor) => {
    setState(s => ({ ...s, selectedColor: color, usingCustomColor: false }));
  };

  const handleCustomColorChange = (hex: string) => {
    setState(s => ({ ...s, customHex: hex, usingCustomColor: true }));
  };

  const handleCustomHexInput = (value: string) => {
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      handleCustomColorChange(value);
    }
  };

  const handleSave = async () => {
    if (!hasChanges) {
      navigate(-1);
      return;
    }

    setIsSaving(true);
    try {
      // If using custom color, save it; otherwise use preset and clear custom
      const customHexToSave = state.usingCustomColor ? state.customHex : undefined;
      const colorToSave = state.selectedColor ?? state.currentColor;
      await updateAvatarColor(appUser.uid, colorToSave, customHexToSave);
      setState(s => ({
        ...s,
        currentColor: s.selectedColor ?? s.currentColor,
        selectedColor: null,
        originalCustomHex: s.customHex,
      }));
      // Wait a moment to ensure Firestore write is committed
      await new Promise(resolve => setTimeout(resolve, 300));
      await refetchAppUser();
      toast.success(t('userProfile.colorSaved'));
      navigate(-1);
    } catch (error) {
      console.error('Failed to update avatar color:', error);
      toast.error(t('userProfile.colorSaveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const initials = getInitials(state.displayName);
  const customGradient = `linear-gradient(135deg, ${adjustBrightness(state.customHex, 1.3)} 0%, ${state.customHex} 100%)`;

  // Check if custom color or preset has changed
  const hasCustomColorChanged = state.usingCustomColor && state.customHex !== state.originalCustomHex;
  // In preset mode, enable save if a color is selected (user made an explicit choice)
  const hasPresetColorChanged = !state.usingCustomColor && state.selectedColor !== null;
  const hasChanges = hasCustomColorChanged || hasPresetColorChanged;

  const heroColor = state.selectedColor ?? state.currentColor;
  const heroCustomGradient = state.usingCustomColor ? customGradient : undefined;

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar
        displayName={user?.displayName ?? null}
        onSignOut={signOut}
        onLogoClick={() => navigate('/')}
        onAvatarClick={() => navigate('/profile')}
      />

      <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        {/* Hero Section */}
        <div className="mb-8 flex h-[220px] flex-col items-center justify-center rounded-2xl bg-gradient-to-br from-green-50 to-green-100 px-6 text-center">
          <div className="relative">
            <Avatar
              size="lg"
              variant="user"
              avatarColor={heroColor}
              customGradient={heroCustomGradient}
              className="shadow-lg"
            >
              {initials}
            </Avatar>
          </div>
          <h1 className="mt-2 font-display text-lg font-semibold text-gray-900">
            {state.displayName}
          </h1>
          <p className="font-body text-xs text-gray-600">
            {state.email}
          </p>
        </div>

        {/* Preferences Card */}
        <div className="mb-4 rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-8 py-5">
            <h2 className="font-display text-lg font-semibold text-gray-900">
              {t('userProfile.preferences')}
            </h2>
          </div>
          <div className="px-8 py-6">
            <div className="flex items-center justify-between gap-4">
              <label className="font-body text-sm font-medium text-gray-700">
                {t('userProfile.languageLabel')}
              </label>
              <select
                value={i18n.language}
                onChange={e => { void i18n.changeLanguage(e.target.value); }}
                className="rounded-sm border border-gray-300 bg-white px-3 py-2 font-body text-sm text-gray-900 outline-none transition focus:border-green-600 focus:ring-4 focus:ring-green-600/15"
              >
                {LANGUAGE_CODES.map(code => (
                  <option key={code} value={code}>{t(`language.${code}`)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white">
          {/* Card Header */}
          <div className="border-b border-gray-100 px-8 py-5">
            <h2 className="font-display text-lg font-semibold text-gray-900">
              {t('userProfile.selectColor')}
            </h2>
            <p className="mt-1 font-body text-sm text-gray-600">
              {t('userProfile.subtitle')}
            </p>
          </div>

          <div className="px-8 py-8">
            {/* Preset Colors */}
            <div className="mb-8">
              <div className="mb-5 text-3xs font-bold uppercase tracking-widest text-gray-500">
                {t('userProfile.presetColors')}
              </div>
              <div className="grid grid-cols-5 gap-6 sm:grid-cols-5">
                {AVATAR_COLORS.map(color => (
                  <div key={color.id} className="flex flex-col items-center gap-3">
                    <button
                      onClick={() => handleColorSelect(color.id)}
                      className={`rounded-full transition-all duration-200 ${
                        state.selectedColor === color.id
                          ? 'ring-2 ring-green-400 shadow-md scale-110'
                          : 'hover:scale-110'
                      }`}
                    >
                      <Avatar size="md" variant="user" avatarColor={color.id}>
                        {initials}
                      </Avatar>
                    </button>
                    <span className="font-body text-3xs font-medium text-gray-700 text-center">
                      {color.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Color Section */}
            <div className="border-t border-gray-100 pt-8">
              <div className="mb-5 text-3xs font-bold uppercase tracking-widest text-gray-500">
                {t('userProfile.customColor')}
              </div>
              <div className="flex items-start gap-6 sm:items-center">
                <div className="flex-shrink-0">
                  <div className="relative inline-block">
                    <div
                      className="cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105"
                      onClick={() => colorInputRef.current?.click()}
                    >
                      <Avatar
                        size="md"
                        variant="user"
                        avatarColor={state.currentColor}
                        customGradient={state.usingCustomColor ? customGradient : undefined}
                      >
                        {initials}
                      </Avatar>
                    </div>
                    <div className="absolute bottom-0 right-0 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm text-gray-700">
                      <Icon size="xs">
                        <PipetteIcon />
                      </Icon>
                    </div>
                  </div>
                  <input
                    ref={colorInputRef}
                    type="color"
                    value={state.customHex}
                    onChange={e => handleCustomColorChange(e.target.value)}
                    style={{ width: '0', height: '0', opacity: '0', cursor: 'pointer' }}
                  />
                </div>
                <div className="flex-1">
                  <div>
                    <label className="text-3xs font-semibold tracking-wider text-gray-700 mr-2">
                      {t('userProfile.hexValue')}
                    </label>
                    <input
                      type="text"
                      value={state.customHex}
                      onChange={e => handleCustomHexInput(e.target.value)}
                      placeholder="#a3e635"
                      className="mt-3 w-full max-w-[140px] rounded-md border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900 transition-all focus:border-green-500 focus:ring-4 focus:ring-green-100"
                    />
                  </div>
                  <div className="mt-3 font-body text-sm text-gray-500">
                    {t('userProfile.pickOrEnter')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(-1)}
            disabled={isSaving}
          >
            {t('userProfile.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? t('userProfile.saving') : `💾 ${t('userProfile.saveColor')}`}
          </Button>
        </div>
      </main>
    </div>
  );
}
